import { FC, useEffect, useRef, useState } from 'react';
import { Input, Collapse, Pagination, Checkbox } from 'antd';
import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import InfrastructureService from '../../domain/Infrastructure/InfraestructureService';
import type { PaginationProps } from 'antd';
import './InfrastructureSelector.scss';
import SelectTypeInfrastructure from '@/basicsComponents/Selects/SelectTypeInfrastructure';

const { Panel } = Collapse;
const infrastructureService = new InfrastructureService();

interface SelectedParameter {
  paremeterId: string;
  paremeterName: string;
  infrastructureId: string;
  infrastructureTypeId: string;
  locationId: string;
  infraestructureName: string;
}

interface InfrastructureSelectorProps {
  locationId: string | null;
  onSelectedParameters: (parameters: SelectedParameter[]) => void;
  initialSelectedParameters?: SelectedParameter[];
}

const InfrastructureSelector: FC<InfrastructureSelectorProps> = ({
  locationId,
  onSelectedParameters,
  initialSelectedParameters = [],
}) => {
  const [mounted, setMounted] = useState(false);
  const [name, setName] = useState<string>('');
  const [parameters, setParameters] = useState([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedParameters, setSelectedParameters] = useState<SelectedParameter[]>(initialSelectedParameters);
  const [selectedInfrastructuresCache, setSelectedInfrastructuresCache] = useState<any[]>([]);
  const [selectedActiveKeys, setSelectedActiveKeys] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
  });

  const [temporalName, setTemporalName] = useState('');
  const selectTypeInfrastructureRef = useRef<any>(null);
  const [selectedInfraType, setSelectedInfraType] = useState<any>(null);

  const handleCollapseChange = (keys: string[] | string) => {
    const newKeys = Array.isArray(keys) ? keys : [keys];
    setActiveKeys(newKeys);
  };

  const handleSelectedCollapseChange = (keys: string[] | string) => {
    const newKeys = Array.isArray(keys) ? keys : [keys];
    setSelectedActiveKeys(newKeys);
  };

  const genExtra = () => (
    <PlusCircleOutlined
      style={{ color: '#04628C' }}
      onClick={(event) => event.stopPropagation()}
      rev={undefined}
    />
  );

  const isParameterChecked = (parameter: any) => {
    return selectedParameters.some(
      (selected) =>
        selected.paremeterId === parameter.paremeterId &&
        selected.locationId === locationId
    );
  };

  const handleUpdateParameters = async (
    name: string | null,
    page: number,
    size: number,
    infraTypeId: number | null,
  ) => {
    if (locationId) {
      const infra: any = await infrastructureService.getInfraestructureInUse(
        locationId,
        page,
        size,
        name,
        null,
        infraTypeId,
        true,
      );
      setPagination(() => {
        return {
          ...pagination,
          totalRecords: infra.totalRecords,
          page: infra.currentPage,
        };
      });
      setParameters((_) =>
        infra.data.map((infra: any) => ({
          ...infra,
          measurementParameters: infra.measurementParameters.map(
            (measurement: any) => ({
              ...measurement,
              checked: isParameterChecked(measurement),
            }),
          ),
        })),
      );
    }
  };

  const onChangePagination: PaginationProps['onChange'] = (page) => {
    setPagination({ ...pagination, page });
    handleUpdateParameters(name, page, pagination.pageSize, selectedInfraType);
  };

  const renderParameterPanel = (
    parameter: any,
    isAlreadySelected: boolean = false,
  ) => {
    const handleCheckboxChange = (target: any, checked: boolean) => {
      let newSelectedParameters: SelectedParameter[];

      if (checked) {
        const newParameter: SelectedParameter = {
          paremeterId: target.paremeterId,
          paremeterName: target.paremeterName,
          infrastructureId: parameter?.infraestructureId,
          infrastructureTypeId: parameter?.infraTypeId,
          locationId: locationId ?? '',
          infraestructureName: parameter?.infraestructureName || 'Unknown Infrastructure',
        };

        newSelectedParameters = [...selectedParameters, newParameter];

        // Add infrastructure to cache if it's the first parameter selected from this infrastructure
        const hasParametersFromThisInfra = selectedParameters.some(
          param => param.infrastructureId === parameter?.infraestructureId
        );
        
        if (!hasParametersFromThisInfra) {
          const infraExists = selectedInfrastructuresCache.some(
            infra => infra.infraestructureId === parameter?.infraestructureId
          );
          
          if (!infraExists) {
            setSelectedInfrastructuresCache(prev => [...prev, parameter]);
          }
        }
      } else {
        newSelectedParameters = selectedParameters.filter(
          (selected) => selected.paremeterId !== target.paremeterId,
        );

        // Remove infrastructure from cache if no more parameters are selected from it
        const remainingParametersFromInfra = newSelectedParameters.filter(
          param => param.infrastructureId === parameter?.infraestructureId
        );

        if (remainingParametersFromInfra.length === 0) {
          setSelectedInfrastructuresCache(prev => 
            prev.filter(infra => infra.infraestructureId !== parameter?.infraestructureId)
          );
        }
      }

      setSelectedParameters(newSelectedParameters);
      onSelectedParameters(newSelectedParameters);
    };

    return (
      <Panel
        header={
          <div className="flex column">
            <span>{parameter?.infraestructureName}</span>
            <span style={{ color: '#74788D', fontSize: '10px' }}>
              {parameter.infrastructureTypeName}
            </span>
          </div>
        }
        key={parameter?.infraestructureId}
        extra={genExtra()}
      >
        <div className="flex column columnapequena">
          {parameter?.measurementParameters?.map((target: any, i: number) => (
            <Checkbox
              key={`checkbox_${i}`}
              checked={selectedParameters.some(
                (selected: SelectedParameter) => 
                  selected.paremeterId === target.paremeterId
              )}
              onChange={(e) => handleCheckboxChange(target, e.target.checked)}
            >
              <span className="parameter-name">{target.paremeterName}</span>
            </Checkbox>
          ))}
        </div>
      </Panel>
    );
  };

  // Get infrastructures with selected parameters from cache
  const getSelectedInfrastructures = () => {
    return selectedInfrastructuresCache.map(cachedInfra => {
      // Get selected parameters for this infrastructure
      const infraSelectedParams = selectedParameters.filter(
        param => param.infrastructureId === cachedInfra.infraestructureId
      );

      // Check if this infrastructure has updated data in current parameters page
      const currentPageInfra = parameters.find(p => p.infraestructureId === cachedInfra.infraestructureId);
      const baseInfra = currentPageInfra || cachedInfra;

      // Show all parameters from the infrastructure, marking which ones are selected
      return {
        ...baseInfra,
        measurementParameters: baseInfra.measurementParameters.map((mp: any) => ({
          ...mp,
          checked: infraSelectedParams.some(sp => sp.paremeterId === mp.paremeterId)
        })),
      };
    });
  };

  function filteredArray(baseArray: any[], selectedInfras: any[]) {
    const ids = new Set(selectedInfras.map((item) => item.infraestructureId));
    return baseArray.filter((item) => !ids.has(item.infraestructureId));
  }

  useEffect(() => {
    const resetState = async () => {
      if (!mounted) {
        setMounted(true);
        return;
      }
      setParameters([]);
      setName('');
      setTemporalName('');
      setPagination({
        page: 1,
        pageSize: 10,
        totalRecords: 0,
      });
      setActiveKeys([]);
      setSelectedActiveKeys([]);
      setSelectedParameters([]);
      setSelectedInfrastructuresCache([]);
      setTimeout(() => {
        handleUpdateParameters(null, 1, 10, selectedInfraType);
      }, 100);
    };

    resetState();
  }, [locationId, mounted]);

  // Update selected parameters when initialSelectedParameters changes
  useEffect(() => {
    if (initialSelectedParameters !== selectedParameters) {
      setSelectedParameters(initialSelectedParameters);
    }
  }, [initialSelectedParameters]);

  // Populate cache with infrastructures that have selected parameters
  useEffect(() => {
    if (selectedParameters.length > 0 && parameters.length > 0) {
      const infraIds = [...new Set(selectedParameters.map(p => p.infrastructureId))];
      const initialInfras = parameters.filter(p => infraIds.includes(p.infraestructureId));
      
      // Add to cache only if not already present
      setSelectedInfrastructuresCache(prevCache => {
        const existingIds = new Set(prevCache.map(c => c.infraestructureId));
        const newInfras = initialInfras.filter(infra => !existingIds.has(infra.infraestructureId));
        return [...prevCache, ...newInfras];
      });
    }
  }, [selectedParameters, parameters]);

  // Sync cache when parameters change (pagination) - update existing cached infrastructures
  useEffect(() => {
    if (selectedInfrastructuresCache.length > 0 && parameters.length > 0) {
      setSelectedInfrastructuresCache(prevCache => 
        prevCache.map(cachedInfra => {
          // Update cached infrastructure with fresh data if it's on current page
          const currentPageInfra = parameters.find(p => p.infraestructureId === cachedInfra.infraestructureId);
          return currentPageInfra || cachedInfra;
        })
      );
    }
  }, [parameters]);

  return (
    <div style={{ height: 'inherit', overflow: 'auto' }} className='py-2'>

      <div className="flex justify-between mr-2 pb-1">
        <Input
          value={temporalName}
          onChange={(e) => {
            setTemporalName(e.target.value);
            if (e.target.value === '') {
              setName('');
              setPagination({ ...pagination, page: 1 });
              handleUpdateParameters(
                null,
                1,
                pagination.pageSize,
                selectedInfraType,
              );
            }
          }}
          className="ml-2"
          style={{ width: 240 }}
          placeholder="Search Name"
          prefix={<SearchOutlined style={{ color: '#078CB3' }} />}
          onPressEnter={(e) => {
            setName(e.currentTarget.value);
            setPagination({ ...pagination, page: 1 });
            handleUpdateParameters(
              e.currentTarget.value,
              1,
              pagination.pageSize,
              selectedInfraType,
            );
          }}
          allowClear
        />
      </div>

      <div className="flex justify-between ml-2 mr-2">
        <div style={{ width: 160 }}>
          <SelectTypeInfrastructure
            ref={selectTypeInfrastructureRef}
            onSelect={(value: any) => {
              setSelectedInfraType(value ?? null);
              setPagination({ ...pagination, page: 1 });
              handleUpdateParameters(name, 1, pagination.pageSize, value);
            }}
          />
        </div>
      </div>

      <div className="mt-3 ml-2 mr-2">
        {selectedParameters.length > 0 && (
          <div className="mb-2">
            <span className="mb-1 font-bold">Selected Parameters</span>
            <Collapse
              activeKey={selectedActiveKeys}
              onChange={handleSelectedCollapseChange}
              expandIconPosition={'start'}
              style={{ overflow: 'auto' }}
            >
              {getSelectedInfrastructures().map((parameter: any) =>
                renderParameterPanel(parameter, true),
              )}
            </Collapse>
          </div>
        )}
        <div>
          {selectedParameters.length > 0 && (
            <span className="mb-1 font-bold">Parameters</span>
          )}
          <Collapse
            activeKey={activeKeys}
            onChange={handleCollapseChange}
            expandIconPosition={'start'}
            style={{ overflow: 'auto' }}
          >
            {filteredArray(parameters, getSelectedInfrastructures()).map(
              (parameter: any) => renderParameterPanel(parameter),
            )}
          </Collapse>
        </div>

        {parameters.length > 0 && (
          <Pagination
            style={{ marginTop: '2rem' }}
            onChange={onChangePagination}
            current={pagination.page}
            total={pagination.totalRecords}
            showSizeChanger={false}
            showLessItems
            itemRender={(page, type, originalElement) => {
              if (type === 'page') {
                if (Math.abs(page - pagination.page) <= 3) {
                  return originalElement;
                }
                return null;
              }
              return originalElement;
            }}
          />
        )}
      </div>
    </div>
  );
};

export default InfrastructureSelector;